import { useQuery } from '@tanstack/react-query';
import { Promise } from 'bluebird';
import { mediaUtil } from '../utils';
import { buildingService } from '../../services/homii-services';
import _ from 'lodash';

async function fetchBuildingsByCityId(cityId, cityName) {    
    try {
        const fetchedBuildings = await buildingService
            .getBuildingByCityId(cityId)
            .then(async (buildings) => {
                if (cityName) {
                    _.forEach(buildings, (building) => {
                        _.set(building, 'city', cityName);
                    });
                }
                const buildingsWithImages = mediaUtil.generateBuildingImageUrls(buildings);
                return buildingsWithImages;
            });

        return fetchedBuildings;
    } catch (error) {        
        throw Error(
            `${_.get(
                error,
                'response.data.message',
                'An error occurred while fetching buildings by city.',
            )} Request ID: ${_.get(error, 'response.headers.x-request-id', 'unknown')}`,
        );
    }   

};

async function fetchAllCityBuildings(fetchedCities){
    const cities = _.map(fetchedCities, (city) => city);
    const allBuildings = [];
    const updateCities = await Promise.map(cities, async (_city) => {
        const city = _city;
        const fetchedBuildings = await buildingService.getBuildingByCityId(city.id);
        const buildings = mediaUtil.generateBuildingImageUrls(fetchedBuildings);
        _.map(buildings, (_building) => {
            const building = _building;
            building.city = city.name;
            mergePrivateAndSharedRoomTypes(building);
        });

        allBuildings.push(buildings);
        city.hasNewBuildings =
            _.indexOf(buildings, { acceptingProvisionalBookings: false }) >= 0;
        return city;        
    });
    const updatedBuildingObjects = {};
    _.forEach(allBuildings, (building) => {
        _.forEach(building, (buildingItem) => {
            const buildingId = buildingItem.id;
            updatedBuildingObjects[buildingId] = buildingItem;
        });
    });
    const updatedCityObjects = {};
    _.forEach(updateCities, (city) => {
        updatedCityObjects[city.id] = city;
    });   
    return {
        buildings: updatedBuildingObjects,
        cities: updatedCityObjects
    };     
}

async function fetchBuildingById(buildingId) {    
    const building = await buildingService
        .getBuildingById(buildingId)
        .then(async (building) => {
            const buildingWithImages = mediaUtil.generateSingleBuildingImageUrls(building);
            return buildingWithImages;    
        });            
    return building;
}

async function fetchAllBuildingsWithImages() {
    const buildings = await buildingService
        .getAllBuildings()
        .then(async (buildings) => {
            const updatedBuildingObjects = {};
            const buildingsWithImages = mediaUtil.generateBuildingImageUrls(buildings);
            _.forEach(buildingsWithImages, (building) => {
                updatedBuildingObjects[building.id] = building;                
            });
            return updatedBuildingObjects;
        });
    return buildings;
};

function mergePrivateAndSharedRoomTypes(building) {
    const privateRoomTypes = _.get(building, 'privateRoomTypes', []);
    const sharedRoomTypes = _.get(building, 'sharedRoomTypes', []);
    const streetAddress = _.get(building, 'address.addressLine1', '');

    _.unset(building, 'privateRoomTypes');
    _.unset(building, 'sharedRoomTypes');

    _.set(privateRoomTypes, 'arrangementType', 'private');
    _.set(sharedRoomTypes, 'arrangementType', 'shared');
    _.set([...privateRoomTypes, ...sharedRoomTypes], 'streetAddress', streetAddress);

    _.set(building, 'roomTypes', [...privateRoomTypes, ...sharedRoomTypes]);
}

export const useBuildingsData = () => {
    return useQuery({
        queryKey: ['buildings'],
        queryFn: fetchAllBuildingsWithImages,
        staleTime: 1000 * 60 * 60, 
    });
};

export default {
    fetchBuildingsByCityId,
    fetchAllCityBuildings,
    fetchBuildingById,
    fetchAllBuildingsWithImages
};


